import { memo, useCallback, useEffect, useReducer, useRef } from 'react'
import { useCloudContext } from '../contexts/CloudProvider.jsx'

// Action types for reducer
const ACTION_TYPES = {
  SET_LOADING: 'SET_LOADING',
  SET_SENT: 'SET_SENT',
  SET_ERROR: 'SET_ERROR',
  SET_CHECKING: 'SET_CHECKING',
  RESET_MESSAGES: 'RESET_MESSAGES',
}

// Initial state
const initialState = {
  resendLoading: false,
  resendSent: false,
  checkingVerification: false,
  error: '',
}

// State reducer for cleaner state management
const stateReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return { ...state, resendLoading: action.payload, error: '' }
    case ACTION_TYPES.SET_SENT:
      return { ...state, resendSent: action.payload, resendLoading: false }
    case ACTION_TYPES.SET_ERROR:
      return { ...state, error: action.payload, resendLoading: false }
    case ACTION_TYPES.SET_CHECKING:
      return { ...state, checkingVerification: action.payload }
    case ACTION_TYPES.RESET_MESSAGES:
      return { ...state, resendSent: false, error: '' }
    default:
      return state
  }
}

// Reusable Alert component - memoized to prevent re-renders
function AlertComponent({ type, icon, children }) {
  return (
    <div className={`alert alert-${type}`} role="alert">
      <i className={`bi ${icon} me-2`}></i>
      {children}
    </div>
  )
}
const Alert = memo(AlertComponent)

// Reusable Step component - memoized to prevent re-renders  
function StepComponent({ number, children, showSpinner = false, brandColor }) {
  return (
    <li className={number === 3 ? 'mb-0 d-flex align-items-center' : 'mb-1'}>
      <i className={`bi bi-${number}-circle me-2`} style={{ ...brandColor, fontSize: '0.9rem' }}></i>
      <span className="me-2">{children}</span>
      {showSpinner && (
        <span 
          className="spinner-border spinner-border-sm text-muted" 
          role="status" 
          aria-hidden="true"
          style={{ width: '10px', height: '10px', opacity: 0.6 }}
          title="Checking verification status..."
        />
      )}
    </li>
  )
}
const Step = memo(StepComponent)

/**
 * Email Verification Page - React 19+ optimized
 * Modern Firebase v11+ implementation with optimized re-renders
 * Blocks access until email verification is complete
 */
function EmailVerificationPage() {
  const [{ resendLoading, resendSent, checkingVerification, error }, dispatch] = useReducer(stateReducer, initialState)
  const timeoutRef = useRef(null)
  const pollingIntervalRef = useRef(null)
  
  const { user, resendVerificationEmail, refreshUser, logout } = useCloudContext()

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current)
    }
  }, [])

  // Auto-check verification status every 3 seconds
  useEffect(() => {
    const checkVerificationStatus = async () => {
      try {
        const refreshedUser = await refreshUser()
        if (refreshedUser && refreshedUser.emailVerified) {
          // Clear the polling interval and proceed
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }
          window.location.reload()
        }
      } catch (error) {
        // Silently fail for auto-checks to avoid annoying the user
        console.warn('Auto verification check failed:', error)
      }
    }

    // Start polling after 5 seconds to give user time to read instructions
    const startPollingTimeout = setTimeout(() => {
      pollingIntervalRef.current = setInterval(checkVerificationStatus, 3000)
    }, 5000)

    return () => {
      clearTimeout(startPollingTimeout)
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [refreshUser])

  // Optimized resend handler with useCallback
  const handleResendEmail = useCallback(async () => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true })
    
    try {
      const result = await resendVerificationEmail()
      if (result?.success) {
        dispatch({ type: ACTION_TYPES.SET_SENT, payload: true })
        // Auto-hide success message
        timeoutRef.current = setTimeout(() => {
          dispatch({ type: ACTION_TYPES.SET_SENT, payload: false })
        }, 5000)
      } else {
        dispatch({ type: ACTION_TYPES.SET_ERROR, payload: result?.message || 'Failed to send verification email.' })
      }
    } catch (error) {
      console.error('Error resending verification email:', error)
      const errorMessage = error.code === 'auth/too-many-requests' 
        ? 'Too many requests. Please wait before requesting another verification email.'
        : 'Failed to send verification email. Please try again.'
      dispatch({ type: ACTION_TYPES.SET_ERROR, payload: errorMessage })
    }
  }, [resendVerificationEmail])

  // Optimized logout handler
  const handleLogout = useCallback(async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Error logging out:', error)
    }
  }, [logout])

  // Handle continue after email verification
  const handleContinue = useCallback(async () => {
    dispatch({ type: ACTION_TYPES.SET_CHECKING, payload: true })
    dispatch({ type: ACTION_TYPES.RESET_MESSAGES })
    try {
      const refreshedUser = await refreshUser()
      
      // Check if email is now verified
      if (refreshedUser && refreshedUser.emailVerified) {
        // Force a page refresh to ensure the new auth state propagates properly
        window.location.reload()
        return
      }
      
      // If still not verified, show helpful message
      dispatch({ 
        type: ACTION_TYPES.SET_ERROR, 
        payload: 'Email not yet verified. Please check your email and click the verification link, then try again.', 
      })
    } catch (error) {
      console.error('Error checking verification status:', error)
      dispatch({ type: ACTION_TYPES.SET_ERROR, payload: 'Failed to check verification status. Please refresh the page.' })
    } finally {
      dispatch({ type: ACTION_TYPES.SET_CHECKING, payload: false })
    }
  }, [refreshUser])

  // Reusable styles to prevent redundancy
  const styles = {
    background: {
      background: `
        linear-gradient(135deg, #0f1419 0%, #1a1d29 25%, #252938 50%, #1a1d29 75%, #0f1419 100%),
        radial-gradient(circle at 20% 50%, rgba(255, 0, 0, 0.03) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(255, 0, 0, 0.02) 0%, transparent 50%)
      `,
      minHeight: '100vh',
    },
    card: {
      background: 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(20px)',
      borderRadius: '20px',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
    },
    logo: {
      height: '32px',
      width: 'auto',
      maxWidth: '140px',
      objectFit: 'contain',
    },
    primaryButton: {
      backgroundColor: '#ff0000',
      borderColor: '#ff0000',
      color: 'white',
      fontWeight: '500',
      padding: '10px',
      borderRadius: '8px',
      fontSize: '0.9rem',
    },
    secondaryButton: {
      padding: '10px',
      borderRadius: '8px',
      fontSize: '0.9rem',
    },
    brandColor: { color: '#ff0000' },
    smallText: { fontSize: '0.85rem' },
    tinyText: { fontSize: '0.8rem' },
  }

  return (
    <div 
      className="min-vh-100 d-flex align-items-center justify-content-center position-relative"
      style={styles.background}
    >
      <div className="container position-relative" style={{ zIndex: 'var(--z-content)' }}>
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-4">
            <div className="card shadow-lg border-0" style={styles.card}>
              <div className="card-body p-4">
                {/* Header */}
                <div className="text-center mb-3">
                  <div className="mb-2">
                    <img 
                      src="/images/Revatek-logo.svg" 
                      alt="Revatek Logo" 
                      style={styles.logo}
                      className="img-fluid"
                    />
                  </div>
                  <h1 className="h5 mb-1 fw-bold" style={styles.brandColor}>
                    Verify Your Email
                  </h1>
                  <p className="text-muted mb-0" style={styles.smallText}>
                    Check your email and click the verification link
                  </p>
                </div>

                {/* Email Info */}
                <div className="mb-3 p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="d-flex align-items-center mb-1">
                    <i className="bi bi-envelope-check me-2" style={{ ...styles.brandColor, fontSize: '0.9rem' }}></i>
                    <small className="text-muted" style={styles.tinyText}>Verification email sent to:</small>
                  </div>
                  <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                    {user?.email}
                  </div>
                </div>

                {/* Alerts */}
                {error && <Alert type="danger" icon="bi-exclamation-triangle">{error}</Alert>}
                {resendSent && <Alert type="success" icon="bi-check-circle">Verification email sent! Please check your inbox.</Alert>}

                {/* Instructions */}
                <div className="mb-3">
                  <h6 className="fw-semibold mb-2" style={{ fontSize: '0.9rem' }}>Next Steps:</h6>
                  <ol className="list-unstyled mb-0" style={styles.smallText}>
                    <Step number={1} brandColor={styles.brandColor}>Check your email inbox</Step>
                    <Step number={2} brandColor={styles.brandColor}>Click the verification link</Step>
                    <Step number={3} brandColor={styles.brandColor}>Return here and click &ldquo;Continue&rdquo;</Step>
                  </ol>
                </div>

                {/* Action Buttons */}
                <div className="d-grid gap-2">
                  <button
                    type="button"
                    className="btn"
                    style={styles.primaryButton}
                    onClick={handleContinue}
                    disabled={checkingVerification}
                  >
                    {checkingVerification ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Checking verification...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-right-circle me-2"></i>
                        Continue
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    style={styles.secondaryButton}
                    onClick={handleResendEmail}
                    disabled={resendLoading || resendSent}
                  >
                    {resendLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sending...
                      </>
                    ) : resendSent ? (
                      <>
                        <i className="bi bi-check me-2"></i>
                        Email Sent!
                      </>
                    ) : (
                      <>
                        <i className="bi bi-envelope me-2"></i>
                        Resend Email
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    style={styles.secondaryButton}
                    onClick={handleLogout}
                  >
                    <i className="bi bi-box-arrow-right me-2"></i>
                    Sign Out
                  </button>
                </div>

                {/* Help Text */}
                <div className="text-center mt-3">
                  <small className="text-muted" style={styles.tinyText}>
                    After clicking the verification link in your email, we&apos;ll automatically check for verification or you can click &ldquo;Continue&rdquo; above.
                  </small>
                  <br />
                  <small className="text-muted" style={styles.tinyText}>
                    Didn&apos;t receive it? Check spam or{' '}
                    <button 
                      className="btn btn-link p-0" 
                      style={{ 
                        ...styles.brandColor, 
                        textDecoration: 'none',
                        fontSize: 'inherit',
                        verticalAlign: 'baseline',
                      }}
                      onClick={handleResendEmail}
                      disabled={resendLoading}
                    >
                      resend
                    </button>
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default memo(EmailVerificationPage)
